import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { History } from 'lucide-react'

import { cn } from '@/lib/utils'
import { evalKeys } from '@/lib/queryKeys'
import { CopyButton } from '@/components/ui/copy-button'
import {
  DutyCard,
  DutyCardContent,
  DutyCardHeader,
} from '@/features/duties/components/cards/shared/DutyCard'
import {
  EVAL_RUN_STATUS_CLASS_NAMES,
  formatEvalRunCreatedAt,
  formatEvalRunDateTime,
  formatEvalRunStatus,
  isActiveEvalRun,
} from '../../evalRunPresentation'
import { fetchEvalCaseRuns } from '../../api'
import { EvalRunAssertionStatsCell } from '../EvalRunAssertionStatsCell'
import { EvalRunDetailSheet } from '../EvalRunDetailSheet'

type EvalRunsCardProps = {
  caseId: string
  hasAssertions: boolean
}

export function EvalRunsCard({ caseId, hasAssertions }: EvalRunsCardProps) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)

  const runsQuery = useQuery({
    queryKey: evalKeys.caseRuns(organization?.id, caseId),
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchEvalCaseRuns(token, caseId)
    },
    refetchInterval: (query) => {
      if (!query.state.data?.items.some(isActiveEvalRun)) return false
      return 3000
    },
    staleTime: 5000,
    refetchOnWindowFocus: false,
  })

  const runs = runsQuery.data?.items ?? []
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null

  return (
    <DutyCard>
      <DutyCardHeader
        icon={<History />}
        title="Previous runs"
        action={
          runs.length > 0 ? (
            <span className="text-[12px] font-semibold text-neutral-low-contrast">
              {runs.length}
            </span>
          ) : null
        }
      />
      <DutyCardContent>
        {runsQuery.isLoading ? (
          <p
            role="status"
            className="m-0 text-[13px] text-neutral-low-contrast">
            Loading runs...
          </p>
        ) : runsQuery.isError ? (
          <p role="alert" className="text-xs text-destructive">
            Unable to load runs for this test case.
          </p>
        ) : runs.length === 0 ? (
          <p className="m-0 text-[13px] text-neutral-low-contrast">
            No runs yet.
          </p>
        ) : (
          <div className="overflow-x-auto overflow-y-hidden">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-[11.5px] font-bold uppercase tracking-[0.4px] text-neutral-low-contrast">
                <tr>
                  <th className="px-3 py-3">Run ID</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Results</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-3 py-3">Ended</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedRunId(run.id)}>
                    <td className="whitespace-nowrap px-3 py-3">
                      <div className="group inline-flex items-center gap-1">
                        <button
                          type="button"
                          aria-label={`Open run ${run.id}`}
                          title={run.id}
                          className="rounded-sm font-mono text-xs font-semibold text-primary-high-contrast outline-none focus-visible:ring-2 focus-visible:ring-primary-solid"
                          onClick={(event) => {
                            event.stopPropagation()
                            setSelectedRunId(run.id)
                          }}>
                          {run.id.slice(0, 8)}
                        </button>
                        {/* Stop propagation synchronously — CopyButton's own
                            onClick fires after the clipboard write, when the
                            bubble has already reached the row. */}
                        <span
                          role="presentation"
                          className="inline-flex"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}>
                          <CopyButton
                            content={run.id}
                            label="Run ID"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
                          EVAL_RUN_STATUS_CLASS_NAMES[run.status]
                        )}>
                        {formatEvalRunStatus(run.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <EvalRunAssertionStatsCell
                        run={run}
                        caseHasAssertions={hasAssertions}
                      />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-[13px] text-neutral-low-contrast">
                      {formatEvalRunCreatedAt(run.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-[13px] text-neutral-low-contrast">
                      {formatEvalRunDateTime(run.started_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-[13px] text-neutral-low-contrast">
                      {formatEvalRunDateTime(run.ended_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DutyCardContent>

      <EvalRunDetailSheet
        run={selectedRun}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedRunId(null)
        }}
      />
    </DutyCard>
  )
}
